import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionTitle } from '@/components/common/SectionTitle';
import { FacultyCard } from '@/components/common/FacultyCard';
import { ManagementCard } from '@/components/common/ManagementCard';
import { fetchFaculty } from '@/store/slices/facultySlice';

const FacultyPage = () => {
  const dispatch = useDispatch();
  const { data, fetchLoading, error } = useSelector((state) => state.faculty);

  useEffect(() => {
    dispatch(fetchFaculty());
  }, [dispatch]);

  const getAvatar = (name) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Staff')}&background=random&size=300`;

  const apiStaff = data?.data || [];

  /* API SHAPE → UI SHAPE (common mapper) */
  const mapStaff = (f) => ({
    id: f.id,
    name: f.name,
    designation: f.designation,
    qualification: f.qualification,
    experience: `${f.experience_years} Years`,
    specialization: f.department || f.specialization,
    achievements: (f.specialization || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    image: f.photo || getAvatar(f.name)
  });

  const allStaff = apiStaff.map(mapStaff);

  const isDirector = (designation = '') =>
    designation.toLowerCase().includes('managing director') &&
    !designation.toLowerCase().includes('admin');

  const isAdminHead = (designation = '') =>
    designation.toLowerCase().includes('admin');

  const directorList = allStaff.filter((s) => isDirector(s.designation));
  const adminHeadList = allStaff.filter((s) => isAdminHead(s.designation));
  const teachingFaculty = allStaff.filter(
    (s) => !isDirector(s.designation) && !isAdminHead(s.designation)
  );

  return (
    <div>
      <PageHeader
        title="Our Expert Faculty"
        subtitle="Meet the mentors who transform student aspirations into achievements"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Faculty' }
        ]}
      />

      {/* MANAGEMENT SECTION */}
      {directorList.length > 0 && (
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <SectionTitle
              title="Director"
              description="Visionary leadership guiding students toward success"
              className="mb-16"
            />
            <div className="flex justify-center">
              {directorList.map((person) => (
                <ManagementCard key={person.id} person={person} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionTitle
            title="Experienced & Dedicated Teachers"
            description="Our faculty comprises highly qualified professionals with years of teaching experience"
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teachingFaculty.map((faculty, index) => (
              <FacultyCard key={faculty.id} faculty={faculty} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ADMINISTRATION */}
      {adminHeadList.length > 0 && (
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <SectionTitle
              title="Administration"
              description="Ensuring seamless academic operations"
              className="mb-16"
            />
            <div className="flex justify-center">
              {adminHeadList.map((person) => (
                <ManagementCard key={person.id} person={person} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default FacultyPage;